/**
 * Appin datalähteestä saapuvan DOKUMENTIN tulkinta parent-puolella (WP-KU3).
 *
 * <p>Julkaistu App ei voi katsella eikä ladata tiedostoa itse: sen iframe on
 * `sandbox="allow-scripts"` ilman `allow-downloads`-lupaa, ja CSP on
 * `default-src 'none'` ilman `frame-src`/`object-src` — eli `<iframe>`, `<embed>`
 * ja `<object>` ovat estettyjä myös `data:`- ja `blob:`-URL:eilla. Siksi App
 * nimeää vain **aikeen ja datalähteen id:n**, ja parent (portaali) hakee
 * tavut omalla tokenillaan ja renderöi ne. Sama luottamusmalli kuin
 * `open-link`illä; ks. {@link ./bridgeHost}.
 *
 * Tämä moduuli on se osa jota ei tarvitse ajaa selaimessa: vastausrungon
 * tulkinta, tiedostonimen siivous ja base64:n purku. Puhtaita funktioita,
 * jotta turvasäännöt ovat yksikkötestattavissa ilman DOMia.
 */

/** Parentin ymmärtämä dokumentti: nimi, tyyppivihje ja tavut. */
export interface AppDocument {
  /** Siivottu tiedostonimi — ei polkuerottimia eikä ohjausmerkkejä. */
  filename: string;
  /** Datalähteen ilmoittama sisältötyyppi (VIHJE; tyyppi ratkaistaan uudelleen). */
  contentType: string | null;
  /** Puretut tavut. */
  bytes: Uint8Array;
}

/**
 * Yläraja purettavalle dokumentille. `AppInvokeService` kattaa vastauksen jo
 * 2 MB:hen, joten tämä ei ole ensisijainen portti vaan varmistus siltä varalta
 * että kutsupolku joskus muuttuu — parentin muistia ei täytetä Appin päätöksellä.
 */
export const MAX_DOCUMENT_BYTES = 8 * 1024 * 1024;

/** Kenttien synonyymit: sama dokumentti voi tulla usealla luontevalla nimellä. */
const NAME_KEYS = ['name', 'filename', 'fileName', 'tiedostonimi'];
const TYPE_KEYS = ['type', 'contentType', 'mimeType'];
const DATA_KEYS = ['dataBase64', 'base64', 'data', 'content'];

/**
 * Poimi dokumentti datalähteen vastausrungosta.
 *
 * <p>Hyväksytty muoto on **sama kuin `file-upload`-komponentin julkaisema**
 * (`{ name, type, size, dataBase64 }`), jotta tiedosto menee sisään ja tulee
 * ulos samanmuotoisena. Lisäksi siedetään tavallisimmat synonyymit
 * (`fileName`/`contentType`/`base64`), koska reitin kirjoittaja päätyy niihin
 * luontevasti — mutta EI mielivaltaisia kenttiä: hiljaa väärästä kentästä
 * luettu tiedosto olisi pahempi kuin selkeä virhe.
 *
 * <p>Runko voi olla objekti, taulukko tai `{rows|data|items|results: [...]}`
 * (sama kuori kuin tauluilla, {@link ../runtime/dataSources}) — silloin
 * luetaan ENSIMMÄINEN alkio.
 *
 * @throws Error kun runkoa ei voi tulkita dokumentiksi
 */
export function parseAppDocument(responseBody: string): AppDocument {
  let parsed: unknown;
  try {
    parsed = JSON.parse(responseBody);
  } catch {
    throw new Error('Datalähde ei palauttanut JSONia');
  }
  const row = unwrapRow(parsed);
  if (!row) throw new Error('Datalähteen vastauksesta ei löytynyt dokumenttia');

  const rawData = pick(row, DATA_KEYS);
  if (typeof rawData !== 'string' || rawData.trim() === '') {
    throw new Error('Dokumentista puuttuu sisältö (dataBase64)');
  }
  const rawName = pick(row, NAME_KEYS);
  const rawType = pick(row, TYPE_KEYS);

  return {
    filename: safeFilename(typeof rawName === 'string' ? rawName : ''),
    contentType: typeof rawType === 'string' && rawType.trim() !== '' ? rawType.trim() : null,
    bytes: decodeBase64(rawData),
  };
}

/** Objekti sellaisenaan, tai listan ensimmäinen alkio. */
function unwrapRow(parsed: unknown): Record<string, unknown> | null {
  if (Array.isArray(parsed)) {
    return isRow(parsed[0]) ? (parsed[0] as Record<string, unknown>) : null;
  }
  if (!isRow(parsed)) return null;
  const obj = parsed as Record<string, unknown>;
  // Suora osuma voittaa kuoren: {dataBase64: "..."} on jo dokumentti. Osuman on
  // oltava MERKKIJONO: `data` on sekä sisältökenttä että yleinen listakuori
  // ({data: [...]}), ja pelkkä avaimen olemassaolo lukisi kuoren dokumentiksi.
  if (typeof pick(obj, DATA_KEYS) === 'string') return obj;
  for (const key of ['rows', 'data', 'items', 'results']) {
    const v = obj[key];
    if (Array.isArray(v) && isRow(v[0])) return v[0] as Record<string, unknown>;
  }
  return obj;
}

function isRow(v: unknown): boolean {
  return v != null && typeof v === 'object' && !Array.isArray(v);
}

function pick(row: Record<string, unknown>, keys: string[]): unknown {
  for (const k of keys) {
    if (row[k] !== undefined && row[k] !== null) return row[k];
  }
  return undefined;
}

/**
 * Siivoa tiedostonimi tallennuskelpoiseksi. Nimi tulee Appin datalähteestä eli
 * käytännössä reitin kirjoittajalta, eikä sitä saa käyttää sellaisenaan
 * `<a download>`-attribuutissa: polkuerottimet ja ohjausmerkit pois, pituus
 * rajattu. Tyhjä nimi saa neutraalin oletuksen — nimetön lataus on selaimessa
 * hämmentävämpi kuin geneerinen nimi.
 */
export function safeFilename(raw: string): string {
  const base = raw.split(/[/\\]/).pop() ?? '';
  const cleaned = base
    .replace(/[\u0000-\u001f\u007f]/g, '')
    .replace(/^\.+/, '')
    .trim();
  if (!cleaned) return 'dokumentti';
  return cleaned.length > 200 ? cleaned.slice(0, 200) : cleaned;
}

/**
 * Pura base64 tavuiksi.
 *
 * <p>`atob` on **lepsu samaan tapaan kuin Javan `Base64.getMimeDecoder()`**:
 * se hyväksyy osan roskasta hiljaa. Siksi syöte tarkistetaan aakkostoa vasten
 * ENNEN purkua — muuten kelvoton vastaus tallentuisi levylle "onnistuneesti"
 * ja vika näkyisi vasta tiedostoa avattaessa.
 */
export function decodeBase64(raw: string): Uint8Array {
  // data:-URL-etuliite siedetään: se on yleisin muoto jossa base64 liikkuu.
  const comma = raw.indexOf(',');
  const payload = (raw.startsWith('data:') && comma >= 0 ? raw.slice(comma + 1) : raw)
    .replace(/\s+/g, '');
  if (!/^[A-Za-z0-9+/]*={0,2}$/.test(payload) || payload.length % 4 !== 0) {
    throw new Error('Dokumentin sisältö ei ole kelvollista base64:ää');
  }
  const estimated = (payload.length / 4) * 3;
  if (estimated > MAX_DOCUMENT_BYTES) {
    throw new Error(`Dokumentti on liian suuri (yli ${Math.floor(MAX_DOCUMENT_BYTES / 1024 / 1024)} MB)`);
  }
  const binary = atob(payload);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return bytes;
}

/**
 * Konservatiivinen MIME blob-URL:lle.
 *
 * <p>Vastausheaderiin tai selaimen sniffaukseen ei luoteta: blob-URL **perii
 * sovelluksen originin**, joten HTML:ksi tulkittu dokumentti ajaisi skriptinsä
 * portaalin originissa. Siksi mikään ei koskaan saa tyyppiä `text/html`.
 * Portaalin katselinpolku käyttää tämän sijaan `components/docPreview.ts`:n
 * `resolveMime`-funktiota, jossa sama sääntö on jo kovetettu ja testattu;
 * tämä on standalone-servingin (ei parenttia, ei katselinta) vastine.
 */
export function documentBlobType(filename: string, contentType: string | null): string {
  const ext = filename.includes('.') ? filename.split('.').pop()!.toLowerCase() : '';
  const ct = (contentType ?? '').split(';')[0].trim().toLowerCase();
  if (ext === 'pdf' || ct === 'application/pdf') return 'application/pdf';
  if (ext === 'svg' || ct === 'image/svg+xml') {
    // SVG voi kantaa skriptin. Se kelpaa <img>:iin muttei omaan välilehteen,
    // joten standalone-polulla se ladataan eikä avata.
    return 'application/octet-stream';
  }
  if (ct.startsWith('image/')) return ct;
  if (['png', 'jpg', 'jpeg', 'gif', 'webp', 'avif', 'bmp'].includes(ext)) {
    return `image/${ext === 'jpg' ? 'jpeg' : ext}`;
  }
  return 'application/octet-stream';
}

/** true kun tyypin voi turvallisesti avata omaan välilehteen blob-URL:nä. */
export function isViewableInTab(blobType: string): boolean {
  return blobType === 'application/pdf' || blobType.startsWith('image/');
}

/**
 * Yläraja Apin ITSE tuottamalle vientitekstille (merkkeinä).
 *
 * <p>Sama luku kuin {@link MAX_DOCUMENT_BYTES}, mutta eri yksikkö: tässä
 * mitataan JS-merkkijonoa, ei purettuja tavuja. CSV on käytännössä
 * ASCII-valtaista, joten merkkimäärä on tavumäärän yläraja riittävällä
 * tarkkuudella — tarkempi mittaus ei toisi mitään, koska raja on mielivaltainen
 * turvaraja eikä formaatin ominaisuus.
 *
 * <p>HUOM: merkkijono on jo ylittänyt postMessagen (structured clone kopioi sen)
 * siinä vaiheessa kun parent näkee sen. Raja EI siis estä kopiota vaan rajaa
 * blobin: parentin muistia ei täytetä Apin päätöksellä.
 */
export const MAX_TEXT_EXPORT_CHARS = MAX_DOCUMENT_BYTES;

/** Apin tuottaman tekstiviennin formaatit. Enum, ei vapaa MIME — ks. alla. */
export type TextExportFormat = 'csv';

/**
 * Pakotettu sisältötyyppi Apin tuottamalle tekstille — **ei koskaan Apin
 * ilmoittama**.
 *
 * <p>Sama syy kuin {@link documentBlobType}illä: blob-URL perii portaalin
 * originin, joten `text/html` ajaisi Apin skriptin siellä. Siksi tyyppi
 * johdetaan formaattienumista eikä mistään mitä iframe lähettää — vapaata
 * MIME-merkkijonoa ei oteta vastaan lainkaan, jolloin sitä ei voi myöskään
 * vahingossa kaiuttaa läpi.
 */
export function textBlobType(_format: TextExportFormat): string {
  return 'text/csv;charset=UTF-16LE';
}

/**
 * Tekstivienti tavuina: **UTF-16LE + FF FE -BOM**.
 *
 * <p>Excelissä merkistön ja erottimen tunnistus ovat kytköksissä, eikä UTF-8
 * pysty molempiin (mitattu tuotannossa samalle asiakkaalle 2026-09-04,
 * contact-form-api:n viikkokoosteessa):
 *
 * <table>
 *   <tr><th>Yritys</th><th>Sarakkeet</th><th>ä/ö</th></tr>
 *   <tr><td>UTF-8 + BOM + `;`</td><td>rikki (kaikki A-sarakkeessa)</td><td>OK</td></tr>
 *   <tr><td>UTF-8 + BOM + `sep=;`-rivi</td><td>OK</td><td><b>rikki</b> (`Kyllä` → `KyllÃ¤`)</td></tr>
 *   <tr><td><b>UTF-16LE + BOM + sarkain</b></td><td>OK</td><td>OK</td></tr>
 * </table>
 *
 * <p>Syy: `sep=`-rivin koodipolku EI konsultoi BOMia vaan putoaa järjestelmän
 * ANSI-koodisivuun. UTF-16:n BOM taas on yksikäsitteinen — Excel ei voi luulla
 * sitä ANSI:ksi — ja sarkain on erotin jota Excel kunnioittaa lokaalin
 * listaerottimesta riippumatta. Tiedosto on 2× kokoinen; raportille se on
 * merkityksetöntä.
 *
 * <p>`TextEncoder` osaa vain UTF-8:aa, joten UTF-16LE kirjoitetaan itse:
 * jokainen koodiyksikkö little-endianina, BOM eteen. Surrogaattiparit
 * (emojit) menevät läpi oikein, koska `charCodeAt` palauttaa juuri ne
 * koodiyksiköt jotka UTF-16 tallentaa.
 */
export function textExportBytes(content: string): Uint8Array {
  const out = new Uint8Array(2 + content.length * 2);
  out[0] = 0xff;
  out[1] = 0xfe;
  for (let i = 0; i < content.length; i += 1) {
    const code = content.charCodeAt(i);
    out[2 + i * 2] = code & 0xff;
    out[3 + i * 2] = code >>> 8;
  }
  return out;
}

/**
 * Varmista tiedostopääte siivotun nimen perään.
 *
 * <p>Tarpeen koska {@link safeFilename} voi tyhjentää nimen kokonaan tai pudottaa
 * päätteen: `'..'` → `'dokumentti'`, joka ilman tätä latautuisi päätteettömänä.
 */
export function ensureExtension(name: string, format: TextExportFormat): string {
  const ext = `.${format}`;
  return name.toLowerCase().endsWith(ext) ? name : `${name}${ext}`;
}
